/*
This software is Copyright ©️ 2020 The University of Southern California. All Rights Reserved. 
Permission to use, copy, modify, and distribute this software and its documentation for educational, research and non-profit purposes, without fee, and without a written agreement is hereby granted, provided that the above copyright notice and subject to the full license file found in the root of this software deliverable. Permission to make commercial use of this software may be obtained by contacting:  USC Stevens Center for Innovation University of Southern California 1150 S. Olive Street, Suite 2300, Los Angeles, CA 90115, USA Email: accounting@stevens.usc.edu

The full terms of this copyright and license should always be found in the root directory of this software deliverable as "license.txt" and if these terms are not found with this software, please contact the USC Stevens Center for the full license.
*/
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";

import {
  fetchLessons,
  updateLesson,
  deleteLesson as _deleteLesson,
} from "../../../api";
import { RootState } from "../../store";
import { Edits, Lesson, LoadStatus } from "../../../types";

export interface LessonsState {
  lessons: Record<string, Lesson>;
  lessonEdits: Record<string, Edits<Lesson>>;
  lessonLoadStatus: LoadStatus;
}

const initialState: LessonsState = {
  lessons: {},
  lessonEdits: {},
  lessonLoadStatus: LoadStatus.NONE,
};

/** Actions */

export const loadLessons = createAsyncThunk(
  "lessons/loadLessons",
  async (_, thunkAPI): Promise<Lesson[]> => {
    const state = thunkAPI.getState() as RootState;
    if (!state.login.user || !state.login.accessToken) {
      throw new Error("login required");
    }
    return await fetchLessons(state.login.accessToken);
  }
);

export const addOrUpdateLesson = createAsyncThunk(
  "lessons/addOrUpdateLesson",
  async (data: Partial<Lesson>, thunkAPI): Promise<Lesson> => {
    const state = thunkAPI.getState() as RootState;
    if (!state.login.user || !state.login.accessToken) {
      throw new Error("login required");
    }
    return await updateLesson(state.login.accessToken, data);
  }
);

export const deleteLesson = createAsyncThunk(
  "lessons/deleteLesson",
  async (id: string, thunkAPI): Promise<void> => {
    if (!id) throw new Error("id required");
    const state = thunkAPI.getState() as RootState;
    if (!state.login.user || !state.login.accessToken) {
      throw new Error("login required");
    }
    await _deleteLesson(state.login.accessToken, id);
  }
);

export const revertLesson = createAsyncThunk(
  "lessons/revertLesson",
  async (id: string, thunkAPI): Promise<Lesson> => {
    if (!id) throw new Error("id required");
    const state = thunkAPI.getState() as RootState;
    if (!state.login.user || !state.login.accessToken)
      throw new Error("login required");
    const edits = state.lessons.lessonEdits[id];
    if (
      !edits ||
      !edits.oldData ||
      `${JSON.stringify(edits.oldData)}` === `${JSON.stringify(edits.newData)}`
    ) {
      throw new Error("no changes to revert");
    }
    return await updateLesson(state.login.accessToken, edits.oldData);
  }
);

export const lessonsSlice = createSlice({
  name: "lessons",
  initialState: initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      // load lessons
      .addCase(loadLessons.pending, (state) => {
        state.lessonLoadStatus = LoadStatus.IN_PROGRESS;
      })
      .addCase(loadLessons.fulfilled, (state, action) => {
        for (const lesson of action.payload) {
          state.lessons[lesson.id] = lesson;
          state.lessonEdits[lesson.id] = {
            oldData: lesson,
          };
        }
        state.lessonLoadStatus = LoadStatus.DONE;
      })
      .addCase(loadLessons.rejected, (state) => {
        state.lessonLoadStatus = LoadStatus.FAILED;
      })
      // add or update lesson
      .addCase(addOrUpdateLesson.fulfilled, (state, action) => {
        const lesson = action.payload;
        if (state.lessons[lesson.id]) {
          state.lessonEdits[lesson.id].newData = lesson;
        } else {
          state.lessonEdits[lesson.id] = {
            oldData: lesson,
            created: true,
          };
        }
        state.lessons[lesson.id] = lesson;
      })
      // delete lesson
      .addCase(deleteLesson.fulfilled, (state, action) => {
        const id = action.meta.arg;
        delete state.lessons[id];
      })
      // revert lesson
      .addCase(revertLesson.fulfilled, (state, action) => {
        const id = action.meta.arg;
        state.lessons[id] = action.payload;
        state.lessonEdits[id].oldData = action.payload;
        state.lessonEdits[id].newData = undefined;
      });
  },
});

export default lessonsSlice.reducer;
